import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import {
  FaBan, FaBox, FaCheckCircle, FaClock, FaDollarSign,
  FaEnvelope, FaFileAlt, FaFileUpload, FaMapMarkerAlt, FaPhone,
  FaSearch, FaShoppingBag, FaStore, FaTimes,
} from "react-icons/fa";
import "../../../assets/css/admin/sellers/SellersList.css";
import ActionDropdown from "../../../components/admin/dropdown/action/ActionDropdown";
import StatCard from "../../../components/admin/statcard/StatCard";
import Button from "../../../components/general/button/Button";
import Pagination from "../../../components/general/pagination/Pagination";
import AdminLayout from "../../../layouts/AdminLayout";
import axios from "axios";
import MyContext from "../../../context/MyContext";
import Swal from "sweetalert2";
import { SellersPageSkeleton } from "../../../components/general/skeleton/SkeletonBlock";

const SellersList = () => {
  const navigate = useNavigate();
  const [showViewModal, setShowViewModal]   = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [currentPage, setCurrentPage]       = useState(1);
  const [itemsPerPage, setItemsPerPage]     = useState(10);
  const [searchTerm, setSearchTerm]         = useState("");
  const [activeTab, setActiveTab]           = useState("all");
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [sellersList, setSellersList]       = useState([]);
  const [productsCurrentPage, setProductsCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage]         = useState(5);
  const [loading, setLoading]               = useState(true);

  const [totalSellers, setTotalSellers]           = useState(0);
  const [verifiedSellers, setVerifiedSellers]   = useState(0);   
  const [pendingApprovals, setPendingApprovals]   = useState(0);
  const [suspendedSellers, setSuspendedSellers]   = useState(0);
  const [notSubmittedSellers, setNotSubmittedSellers] = useState(0);

  const { baseUrl, baseUrlForFiles, authToken } = useContext(MyContext);

  const viewAllSellers = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/seller/viewAllSellers`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({ icon: "error", title: "Error", text: response.data.error });
        } else if (response.data.success == true) {
          setSellersList(response.data.sellers);
          setTotalSellers(response.data.totalSellers);
          setVerifiedSellers(response.data.verifiedSellers);
          setPendingApprovals(response.data.pendingApprovals);
          setSuspendedSellers(response.data.suspendedSellers);
          setNotSubmittedSellers(response.data.notSubmittedSellers);
        }
      })
      .catch(() => {
        setLoading(false);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load Sellers" });
      });
  };

  const toggleSellerStatus = (sellerId) => {
    const formData = new FormData();
    formData.append("id", sellerId);
    axios
      .post(`${baseUrl}/admin/seller/updateStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({ icon: "error", title: "Error", text: response.data.error });
        } else if (response.data.success == true) {
          Swal.fire({ icon: "success", title: "Success", text: "Seller status updated", confirmButtonColor: "#097d43" });
          setShowViewModal(false);
          viewAllSellers();
        }
      })
      .catch(() =>
        Swal.fire({ icon: "error", title: "Error", text: "Failed to update status" })
      );
  };

  const updateKycVerification = (sellerId, kycStatus) => {
    const formData = new FormData();
    formData.append("id",         sellerId);
    formData.append("isVerified", kycStatus);
    axios
      .post(`${baseUrl}/admin/seller/updateKycVerification`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({ icon: "error", title: "Error", text: response.data.error });
        } else if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: kycStatus === "verified" ? "Accepted!" : "Rejected!",
            text: kycStatus === "verified" ? "Seller KYC verified." : "Seller KYC rejected.",
            confirmButtonColor: "#097d43",
          });
          setShowViewModal(false);
          viewAllSellers();
        }
      })
      .catch(() =>
        Swal.fire({ icon: "error", title: "Error", text: "Failed to update KYC." })
      );
  };

  const deleteSeller = (seller) => {
    Swal.fire({
      title: "Delete Seller?",
      text: `${seller.name} account will be permanently deleted. This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const formData = new FormData();
      formData.append("id", seller.id);

      axios
        .post(`${baseUrl}/admin/seller/deleteSeller`, formData, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then((response) => {
          if (response.data.success) {
            Swal.fire({ icon: "success", title: "Deleted!", text: "Seller deleted successfully." });
            viewAllSellers();
          } else {
            Swal.fire({ icon: "error", title: "Error", text: response.data.message });
          }
        })
        .catch(() =>
          Swal.fire({ icon: "error", title: "Error", text: "Failed to delete seller." })
        );
    });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/admin/login");
      return;
    }
    viewAllSellers();
  }, [authToken]);

  const statsData = [
    { icon: FaStore,       iconBg: "linear-gradient(135deg, #e3f2fd, #bbdefb)", iconColor: "#2196f3", label: "Total Sellers",     value: totalSellers.toString(),        badge: "All"     },
    { icon: FaCheckCircle, iconBg: "linear-gradient(135deg, #e8f5f0, #d4ede3)", iconColor: "#0ac46c", label: "Verified",          value: verifiedSellers.toString(),       badge: "Live"    },
    { icon: FaClock,       iconBg: "linear-gradient(135deg, #fff3e0, #ffe0b2)", iconColor: "#ff9800", label: "Pending Approvals", value: pendingApprovals.toString(),    badge: "Review"  },
    { icon: FaFileUpload,  iconBg: "linear-gradient(135deg, #f3e5f5, #e1bee7)", iconColor: "#9c27b0", label: "KYC Not Submitted", value: notSubmittedSellers.toString(), badge: "No Docs" },
    { icon: FaBan,         iconBg: "linear-gradient(135deg, #ffebee, #ffcdd2)", iconColor: "#f44336", label: "Suspended Sellers", value: suspendedSellers.toString(),    badge: "Blocked" },
  ];

  const filteredSellers = sellersList.filter((seller) => {
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      (seller.name     || "").toLowerCase().includes(search) ||
      (seller.email    || "").toLowerCase().includes(search) ||
      (seller.shop_name || "").toLowerCase().includes(search);

    let matchesTab = true;
    if (activeTab === "verified") {
      matchesTab = seller.isVerified === "verified";
    } else if (activeTab === "pending") {
      matchesTab = seller.isVerified === "pending";
    } else if (activeTab === "notSubmitted") {
      matchesTab = !seller.kycdocument;
    }

    return matchesSearch && matchesTab;
  });

  const getActions = (seller) => {
    const actions = [
      {
        icon: "MdVisibility",
        label: "View Details",
        onClick: () => {
          setSelectedSeller(seller);
          setShowViewModal(true);
          setProductsCurrentPage(1);
        },
      },
    ];

    if (!seller.kycdocument) {
      actions.push({
        icon: "MdDelete",
        label: "Delete",
        onClick: () => deleteSeller(seller),
        variant: "danger",
      });
    }

    return actions;
  };

  const getVerificationBadgeClass = (isVerified, hasDocument) => {
    if (!hasDocument) return "ksa-seller-badge-not-verified";
    if (isVerified === "verified")     return "ksa-seller-badge-verified";
    if (isVerified === "pending")      return "ksa-seller-badge-pending";
    if (isVerified === "not_verified") return "ksa-seller-badge-not-verified";
    return "ksa-seller-badge-default";
  };

  const getVerificationLabel = (isVerified, hasDocument) => {
    if (!hasDocument) return "Not Submitted";
    if (isVerified === "verified")     return "Verified";
    if (isVerified === "pending")      return "Pending";
    if (isVerified === "not_verified") return "Not Verified";
    return "Unknown";
  };

  const getAccountBadgeClass = (status) => {
    if (status === "active")    return "ksa-seller-badge-active";
    if (status === "blocked")   return "ksa-seller-badge-blocked";
    if (status === "suspended") return "ksa-seller-badge-suspended";
    return "ksa-seller-badge-default";
  };

  if (loading) {
    return (
      <AdminLayout>
        <SellersPageSkeleton />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="ksa-seller-page-header">
          <div>
            <h1 className="ksa-layout-page-title">Sellers Management</h1>
            <p className="ksa-layout-page-subtitle">
              Manage seller accounts, approvals, and monitor their performance.
            </p>
          </div>
        </div>

        {/* ── FIXED: lg={3} → xl={3}. At "lg" (992px), the sidebar
             (~260px) eats into the real content width, so squeezing
             4-5 cards into a row at that breakpoint causes them to
             overflow/clip. "xl" (1200px) leaves enough room even
             after the sidebar. ── */}
        <Row className="g-4 mb-4 ksa-seller-stats">
          {statsData.map((stat, idx) => (
            <Col key={idx} xs={12} sm={6} xl={3}>
              <StatCard {...stat} />
            </Col>
          ))}
        </Row>

        <div className="ksa-card">
          <div className="ksa-seller-tabs">
            <button
              className={`ksa-seller-tab ${activeTab === "all" ? "active" : ""}`}
              onClick={() => { setActiveTab("all"); setCurrentPage(1); }}
            >
              <FaStore /> All Sellers
              <span className="ksa-tab-badge">{totalSellers}</span>
            </button>
            <button
              className={`ksa-seller-tab ${activeTab === "verified" ? "active" : ""}`}
              onClick={() => { setActiveTab("verified"); setCurrentPage(1); }}
            >
              <FaCheckCircle /> Verified
              <span className="ksa-tab-badge">{verifiedSellers}</span>
            </button>
            <button
              className={`ksa-seller-tab ${activeTab === "pending" ? "active" : ""}`}
              onClick={() => { setActiveTab("pending"); setCurrentPage(1); }}
            >
              <FaClock /> Pending Verification
              <span className="ksa-tab-badge ksa-tab-badge-warning">{pendingApprovals}</span>
            </button>
            <button
              className={`ksa-seller-tab ${activeTab === "notSubmitted" ? "active" : ""}`}
              onClick={() => { setActiveTab("notSubmitted"); setCurrentPage(1); }}
            >
              <FaFileUpload /> KYC Not Submitted
              <span className="ksa-tab-badge">{notSubmittedSellers}</span>
            </button>
          </div>

          <div className="ksa-seller-table-header">
            <div className="ksa-seller-search">
              <FaSearch size={16} />
              <input
                type="text"
                placeholder="Search sellers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="ksa-seller-table-wrapper">
            <table className="ksa-seller-table">
              <thead>
                <tr>
                  <th>SELLER NAME</th>
                  <th>EMAIL</th>
                  <th>SHOP NAME</th>
                  <th>REGISTERED ON</th>
                  <th>VERIFICATION</th>
                  <th>ACCOUNT STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredSellers.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: 20 }}>No sellers found</td>
                  </tr>
                ) : (
                  filteredSellers
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map((seller) => (
                      <tr key={seller.id}>
                        <td className="ksa-seller-name">{seller.name}</td>
                        <td className="ksa-seller-email">{seller.email}</td>
                        <td className="ksa-seller-shop">{seller.shop_name}</td>
                        <td>
                          {new Date(seller.created_at).toLocaleDateString()}{" "}
                          <span style={{ color: "var(--text-muted)", fontSize: 12 }}>
                            {new Date(seller.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </td>
                        <td>
                          <span className={`ksa-seller-status-badge ${getVerificationBadgeClass(seller.isVerified, seller.kycdocument)}`}>
                            {getVerificationLabel(seller.isVerified, seller.kycdocument)}
                          </span>
                        </td>
                        <td>
                          <span className={`ksa-seller-status-badge ${getAccountBadgeClass(seller.status)}`}>
                            {seller.status}
                          </span>
                        </td>
                        <td>
                          <ActionDropdown
                            isOpen={openActionMenu === seller.id}
                            onToggle={() => setOpenActionMenu(openActionMenu === seller.id ? null : seller.id)}
                            actions={getActions(seller)}
                          />
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            totalItems={filteredSellers.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(items) => setItemsPerPage(items)}
          />
        </div>

        {showViewModal && selectedSeller && (
          <div className="ksa-modal-overlay" onClick={() => setShowViewModal(false)}>
            <div
              className="ksa-modal ksa-modal-xl ksa-seller-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-seller-modal-header">
                <h3 className="ksa-seller-modal-title">Seller Profile: {selectedSeller.name}</h3>
                <button className="ksa-modal-close" onClick={() => setShowViewModal(false)}>
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-seller-modal-body">
                <Row className="g-4">
                  <Col xs={12} lg={4}>
                    <div className="ksa-seller-profile-card">
                      <h4 className="ksa-seller-profile-name">{selectedSeller.name}</h4>
                      <p className="ksa-seller-profile-shop">{selectedSeller.shop_name}</p>
                      <div className="ksa-seller-profile-badges">
                        <span className={`ksa-seller-status-badge ${getVerificationBadgeClass(selectedSeller.isVerified, selectedSeller.kycdocument)}`}>
                          {getVerificationLabel(selectedSeller.isVerified, selectedSeller.kycdocument)}
                        </span>
                        <span className={`ksa-seller-status-badge ${getAccountBadgeClass(selectedSeller.status)}`}>
                          {selectedSeller.status}
                        </span>
                      </div>
                      <div className="ksa-seller-profile-info">
                        <div className="ksa-seller-info-item"><FaEnvelope /><span>{selectedSeller.email}</span></div>
                        <div className="ksa-seller-info-item"><FaPhone /><span>{selectedSeller.phone}</span></div>
                        <div className="ksa-seller-info-item"><FaMapMarkerAlt /><span>{selectedSeller.address}</span></div>
                      </div>
                      {selectedSeller.isVerified === "verified" && (
                        <div className="ksa-seller-profile-stats">
                          <div className="ksa-seller-stat-item">
                            <FaBox className="ksa-seller-stat-icon" />
                            <div>
                              <p className="ksa-seller-stat-value">{selectedSeller.kmproducts?.length || 0}</p>
                              <p className="ksa-seller-stat-label">Products</p>
                            </div>
                          </div>
                          <div className="ksa-seller-stat-item">
                            <FaShoppingBag className="ksa-seller-stat-icon" />
                            <div>
                              <p className="ksa-seller-stat-value">{selectedSeller.totalOrders || 0}</p>
                              <p className="ksa-seller-stat-label">Orders</p>
                            </div>
                          </div>
                          <div className="ksa-seller-stat-item">
                            <FaDollarSign className="ksa-seller-stat-icon" />
                            <div>
                              <p className="ksa-seller-stat-value">₨{((selectedSeller.totalSales || 0) / 1000).toFixed(1)}K</p>
                              <p className="ksa-seller-stat-label">Revenue</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </Col>

                  <Col xs={12} lg={8}>
                    <div className="ksa-seller-details-section">
                      <h5 className="ksa-seller-section-title">Shop Details</h5>
                      <div className="ksa-seller-details-grid">
                        <div className="ksa-seller-detail-item">
                          <label>Shop Name</label>
                          <p>{selectedSeller.shop_name}</p>
                        </div>
                        <div className="ksa-seller-detail-item">
                          <label>City</label>
                          <p>{selectedSeller.city}</p>
                        </div>
                        <div className="ksa-seller-detail-item ksa-seller-detail-full">
                          <label>Address</label>
                          <p>{selectedSeller.address}</p>
                        </div>
                        <div className="ksa-seller-detail-item">
                          <label>Joined Date</label>
                          <p>{new Date(selectedSeller.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>

                    {(selectedSeller.isVerified === "pending" ||
                      selectedSeller.isVerified === "verified" ||
                      selectedSeller.isVerified === "not_verified") && (
                      <div className="ksa-seller-details-section">
                        <h5 className="ksa-seller-section-title">
                          <FaFileAlt style={{ marginRight: "8px" }} />
                          KYC Documents
                          {selectedSeller.isVerified === "pending" && (
                            <span className="ksa-seller-badge-pending" style={{ marginLeft: 8, fontSize: 11 }}>
                              Pending Verification
                            </span>
                          )}
                          {selectedSeller.isVerified === "not_verified" && (
                            <span className="ksa-seller-badge-not-verified" style={{ marginLeft: 8, fontSize: 11 }}>
                              Rejected
                            </span>
                          )}
                        </h5>
                        {selectedSeller.kycdocument ? (
                          
                          <a  href={`${baseUrlForFiles}/${selectedSeller.kycdocument}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ksa-seller-pdf-btn"
                          >
                            <FaFileAlt /> View Documents
                          </a>
                        ) : (
                          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>No documents uploaded yet.</p>
                        )}
                      </div>
                    )}

                    {selectedSeller.isVerified === "verified" &&
                      selectedSeller.kmproducts &&
                      selectedSeller.kmproducts.length > 0 && (
                        <div className="ksa-seller-details-section">
                          <h5 className="ksa-seller-section-title">
                            <FaBox style={{ marginRight: "8px" }} />
                            Products ({selectedSeller.kmproducts.length})
                          </h5>
                          <div className="ksa-seller-products-table-wrapper">
                            <table className="ksa-seller-products-table">
                              <thead>
                                <tr>
                                  <th>IMAGE</th>
                                  <th>PRODUCT</th>
                                  <th>CATEGORY</th>
                                  <th>PRICE</th>
                                  <th>STOCK</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedSeller.kmproducts
                                  .slice(
                                    (productsCurrentPage - 1) * productsPerPage,
                                    productsCurrentPage * productsPerPage
                                  )
                                  .map((product, idx) => (
                                    <tr key={idx}>
                                      <td>
                                        <img
                                          src={`${baseUrlForFiles}/${product.image}`}
                                          alt={product.name}
                                          style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6 }}
                                        />
                                      </td>
                                      <td>{product.name}</td>
                                      <td>{product.kmsubcategory?.kmcategory?.categoryname}</td>
                                      <td>₨{product.price}</td>
                                      <td>{product.stock}</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                          <Pagination
                            totalItems={selectedSeller.kmproducts.length}
                            itemsPerPage={productsPerPage}
                            currentPage={productsCurrentPage}
                            onPageChange={(page) => setProductsCurrentPage(page)}
                            onItemsPerPageChange={(items) => setProductsPerPage(items)}
                          />
                        </div>
                      )}
                  </Col>
                </Row>
              </div>

              <div className="ksa-seller-modal-footer">
                <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                  Close
                </Button>
                {selectedSeller.isVerified === "pending" && (
                  <>
                    <Button
                      variant="danger"
                      onClick={() => {
                        Swal.fire({
                          title: "Reject KYC?",
                          text: "This seller's KYC will be marked as rejected.",
                          icon: "warning",
                          showCancelButton: true,
                          confirmButtonColor: "#dc3545",
                          cancelButtonColor: "#6b7280",
                          confirmButtonText: "Yes, Reject",
                        }).then((result) => {
                          if (result.isConfirmed) {
                            updateKycVerification(selectedSeller.id, "not_verified");
                          }
                        });
                      }}
                    >
                      Reject KYC
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => {
                        Swal.fire({
                          title: "Accept KYC?",
                          text: "This seller will be marked as verified.",
                          icon: "question",
                          showCancelButton: true,
                          confirmButtonColor: "#097d43",
                          cancelButtonColor: "#6b7280",
                          confirmButtonText: "Yes, Accept",
                        }).then((result) => {
                          if (result.isConfirmed) {
                            updateKycVerification(selectedSeller.id, "verified");
                          }
                        });
                      }}
                    >
                      Accept KYC
                    </Button>
                  </>
                )}
                {selectedSeller.isVerified === "not_verified" && (
                  <Button
                    variant="primary"
                    onClick={() => {
                      Swal.fire({
                        title: "Accept KYC?",
                        text: "This seller will be marked as verified.",
                        icon: "question",
                        showCancelButton: true,
                        confirmButtonColor: "#097d43",
                        cancelButtonColor: "#6b7280",
                        confirmButtonText: "Yes, Accept",
                      }).then((result) => {
                        if (result.isConfirmed) {
                          updateKycVerification(selectedSeller.id, "verified");
                        }
                      });
                    }}
                  >
                    Accept KYC
                  </Button>
                )}
                {selectedSeller.isVerified === "verified" && (
                  <>
                    {selectedSeller.status === "active" && (
                      <Button
                        variant="warning"
                        onClick={() => {
                          Swal.fire({
                            title: "Suspend Seller?",
                            text: "This seller will be suspended.",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonColor: "#ff9800",
                            cancelButtonColor: "#6b7280",
                            confirmButtonText: "Yes, Suspend",
                          }).then((result) => {
                            if (result.isConfirmed) {
                              toggleSellerStatus(selectedSeller.id);
                            }
                          });
                        }}
                      >
                        Suspend Seller
                      </Button>
                    )}
                    {(selectedSeller.status === "suspended" ||
                      selectedSeller.status === "blocked") && (
                      <Button
                        variant="primary"
                        onClick={() => {
                          Swal.fire({
                            title: "Activate Seller?",
                            text: "This seller will be activated.",
                            icon: "question",
                            showCancelButton: true,
                            confirmButtonColor: "#097d43",
                            cancelButtonColor: "#6b7280",
                            confirmButtonText: "Yes, Activate",
                          }).then((result) => {
                            if (result.isConfirmed) {
                              toggleSellerStatus(selectedSeller.id);
                            }
                          });
                        }}
                      >
                        Activate Seller
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SellersList;
